import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

const API = import.meta.env.VITE_API_URL || "/api";

const TIPOLOGIA_OPTIONS = [
  { value: 100000000, label: "Ordinario" },
  { value: 100000001, label: "Straordinario" },
];

function field(label, children, error) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function select(value, onChange, options, placeholder, disabled) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.id || o.value} value={o.id || o.value}>
          {o.nome || o.label}
        </option>
      ))}
    </select>
  );
}

export default function MobileRichiestaPage() {
  const [params] = useSearchParams();
  const token = params.get("t");

  const [tokenValid, setTokenValid] = useState(null); // null=checking, true, false
  const [rsaList, setRsaList] = useState([]);
  const [tipologie, setTipologie] = useState([]);

  const [form, setForm] = useState({
    rsaId: "",
    tipologiaRichiesta: "",
    tipologiaInterventoId: "",
    oggetto: "",
    note: "",
  });
  const [allegati, setAllegati] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Carica RSA al mount (valida anche il token implicitamente)
  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }
    fetch(`${API}/magic/rsa?t=${encodeURIComponent(token)}`)
      .then((r) => {
        if (r.status === 401) {
          setTokenValid(false);
          return null;
        }
        setTokenValid(true);
        return r.json();
      })
      .then((data) => data && setRsaList(data.value || []))
      .catch(() => setTokenValid(false));
  }, [token]);

  // Catalogo tipologie intervento (globale)
  useEffect(() => {
    if (!token || tokenValid !== true) return;
    fetch(`${API}/magic/tipologie-intervento?t=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => {
        const tutte = d.value || [];
        const generiche = tutte.filter((t) => t.generica);
        const altre = tutte
          .filter((t) => !t.generica)
          .sort((a, b) => (a.nome || "").localeCompare(b.nome || "", "it"));
        setTipologie([...altre, ...generiche]);
      })
      .catch(() => setTipologie([]));
  }, [token, tokenValid]);

  const set = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const validate = () => {
    const e = {};
    if (!form.rsaId) e.rsaId = "Seleziona una RSA";
    if (form.tipologiaRichiesta === "")
      e.tipologiaRichiesta = "Seleziona la tipologia";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const body = new FormData();
      body.append("t", token);
      body.append("rsaId", form.rsaId);
      body.append("tipologiaRichiesta", form.tipologiaRichiesta);
      if (form.tipologiaInterventoId)
        body.append("tipologiaInterventoId", form.tipologiaInterventoId);
      if (form.oggetto) body.append("oggetto", form.oggetto);
      if (form.note) body.append("note", form.note);
      allegati.forEach((f) => body.append("allegati", f));

      const res = await fetch(`${API}/magic/submit`, { method: "POST", body });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Errore nell'invio");
      }
      setSubmitted(true);
    } catch (err) {
      setErrors({ _global: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (!token || tokenValid === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">⛔</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Link non valido
          </h1>
          <p className="text-gray-500 text-sm">
            Il link è scaduto o non valido. Richiedi un nuovo QR code
            all'operatore.
          </p>
        </div>
      </div>
    );
  }

  if (tokenValid === null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Caricamento…</div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow p-8 max-w-sm w-full text-center">
          <div className="text-5xl mb-4">✅</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Richiesta inviata!
          </h1>
          <p className="text-gray-500 text-sm">
            La richiesta è stata inserita e sarà elaborata dal team. Puoi
            chiudere questa pagina.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-blue-700 text-white px-4 py-5 safe-top">
        <div className="flex items-center gap-3">
          <img
            src="/logo.png"
            alt=""
            className="h-8 w-8 object-contain"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <div>
            <p className="text-xs text-blue-200 uppercase tracking-wide">
              Peralba Health Care
            </p>
            <h1 className="text-lg font-bold leading-tight">
              Nuova richiesta intervento
            </h1>
          </div>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="px-4 py-5 space-y-1 max-w-lg mx-auto pb-24"
      >
        {errors._global && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-2">
            {errors._global}
          </div>
        )}

        {field(
          "RSA *",
          select(
            form.rsaId,
            (v) => set("rsaId", v),
            rsaList,
            "Seleziona RSA…",
            false,
          ),
          errors.rsaId,
        )}

        {field(
          "Tipologia richiesta *",
          select(
            form.tipologiaRichiesta,
            (v) => set("tipologiaRichiesta", v),
            TIPOLOGIA_OPTIONS,
            "Seleziona tipologia…",
            false,
          ),
          errors.tipologiaRichiesta,
        )}

        {tipologie.length > 0 &&
          field(
            "Tipologia intervento",
            select(
              form.tipologiaInterventoId,
              (v) => set("tipologiaInterventoId", v),
              tipologie,
              "Seleziona tipologia intervento…",
              false,
            ),
          )}

        {field(
          "Oggetto (opzionale)",
          <input
            type="text"
            value={form.oggetto}
            onChange={(e) => set("oggetto", e.target.value)}
            placeholder="Descrizione breve del problema…"
            className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
            maxLength={200}
          />,
        )}

        {field(
          "Descrizione Intervento",
          <textarea
            value={form.note}
            onChange={(e) => set("note", e.target.value)}
            placeholder="Dettagli aggiuntivi, ubicazione, urgenza…"
            rows={4}
            className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            maxLength={2000}
          />,
        )}

        {field(
          "Foto (opzionale, max 5)",
          <div className="space-y-2">
            <div className="flex gap-2">
              <label className="flex-1 flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl p-3 cursor-pointer active:bg-gray-50">
                <span className="text-xl">📷</span>
                <span className="text-gray-600 text-sm">Scatta foto</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    const nuova = Array.from(e.target.files || []);
                    if (!nuova.length) return;
                    setAllegati((prev) => [...prev, ...nuova].slice(0, 5));
                    e.target.value = "";
                  }}
                />
              </label>
              <label className="flex-1 flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl p-3 cursor-pointer active:bg-gray-50">
                <span className="text-xl">🖼️</span>
                <span className="text-gray-600 text-sm">Galleria</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const selezionate = Array.from(e.target.files || []);
                    if (!selezionate.length) return;
                    setAllegati((prev) =>
                      [...prev, ...selezionate].slice(0, 5),
                    );
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            {allegati.length > 0 && (
              <div className="space-y-1">
                {allegati.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
                  >
                    <span className="text-sm text-gray-700 truncate">
                      📎 {f.name}
                    </span>
                    <button
                      type="button"
                      className="ml-2 text-gray-400 hover:text-red-500 text-lg leading-none"
                      onClick={() =>
                        setAllegati((prev) => prev.filter((_, j) => j !== i))
                      }
                    >
                      ×
                    </button>
                  </div>
                ))}
                <p className="text-xs text-gray-400 text-center">
                  {allegati.length}/5 foto
                </p>
              </div>
            )}
          </div>,
        )}

        {/* Fixed bottom button */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 safe-bottom">
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-700 text-white font-semibold py-4 rounded-xl text-base disabled:opacity-60 active:bg-blue-800"
          >
            {submitting ? "Invio in corso…" : "Invia richiesta"}
          </button>
        </div>
      </form>
    </div>
  );
}
